import { DownloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import { Button, Divider, Input, Space, Tooltip, message } from "antd";
import {
	FC,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { DFPEmpty } from "@/components/empty/dfp-empty";
import { DFPTable } from "@/components/tables/dfp-table";
import {
	type BaseTableAction,
	type BaseTableState,
	baseInitialState,
	baseTableReducer,
} from "@/components/tables/shared-table-state";

import { selfOrganizationAssetTypes } from "@/services/organization-service";
import { exportSubmissionsCsvApi } from "@/services/submission-service";
import {
	type IForm,
	type IOrganization,
	useUserStore,
} from "@/stores/user-store";
import { SubmissionData } from "../../SingleLocation/SubmissionsTab/children/DynamicSubmissionDetailAssetRenderer";
import { ColumnConfigDropdown } from "./ColumnConfigDropdown";
import { getOrgColumns } from "./Columns/SubmissionsColumns";
import { DeleteButton } from "./DeleteButton";
import { DeleteModal } from "./DeleteModal";
import { DeletedSwitch } from "./DeletedSwitch";
import { QASubmissionDraftSelect } from "./QASubmissionDraftSelect";
import { SubmissionTypeSelect } from "./SubmissionTypeSelect";
import { fetchSubmissionTableData } from "./childFunctions/fetchData";
import { getTableProps } from "./childFunctions/getTableProps";
import { initializeColumnsShown } from "./childFunctions/initializeColumnsShown";
import { ChecklistZipExportModal } from "./children/ChecklistZipExportModal";
import { clearOrganizedCache } from "./children/Search";

const { Search } = Input;

/* ---------------------------------------------------------------------
   Types
   --------------------------------------------------------------------- */
export interface Submission {
	id: string | number;
	formIcon: ReactNode;
	asset_tag: string;
	asset_type: string;
	address: string;
	submitting_user: string;
	submitted_date: string;
	isLoading?: boolean;
}

export interface Draft {
	id: string | number;
	address: string;
	submitting_user: string;
	submitted_date: string;
	isLoading?: boolean;
}

interface FilterOptions {
	assetTypes: string[];
}

interface MasterFormKey {
	description: string | null;
	id: number;
	handler_function_key: string | null;
	name: string;
}

interface FormConfig {
	formName: string;
}

interface Form {
	form_id: number;
	config: FormConfig;
	required_access_level: number;
	organization_id: number;
	collections: unknown | null;
	enabled: boolean;
	master_form_key: MasterFormKey;
}

interface SubmissionsTableProps {
	organization: IOrganization;
	browserLocationId: string;
}

/* ---------------------------------------------------------------------
   Reducer Types
   --------------------------------------------------------------------- */
export type SubmissionTableAction =
	| BaseTableAction
	| { type: "SET_VIEWING_DRAFTS"; payload: boolean }
	| { type: "SET_VIEWING_DELETED"; payload: boolean }
	| { type: "SET_FORMS_ARR"; payload: Form[] }
	| { type: "SET_SELECTED_FORM"; payload: number }
	| { type: "SET_SELECTED_FORM_MODE"; payload: string }
	| { type: "SET_SELECTED_SUBMISSION_TYPE"; payload: string }
	| { type: "SET_DATA_FLEET_FORMS"; payload: unknown }
	| { type: "SET_FILTERS"; payload: Record<string, unknown> }
	| { type: "SET_SHOW_DELETE_MODAL"; value: boolean }
	| { type: "SET_FILTER_OPTIONS"; payload: FilterOptions }
	| { type: "BATCH_UPDATE"; payload: SubmissionTableState }
	| { type: "SET_IS_VIEWING_QA_FORM"; payload: boolean }
	| { type: "SET_IS_LOADING"; payload: boolean }
	| { type: "SET_REFETCH_TRIGGER"; payload: number }
	| { type: "SET_COLUMNS_SHOWN"; payload: Record<string, boolean> }
	| { type: "SET_PAGE_NUM"; payload: number }
	| { type: "SET_TABLE_COLUMNS"; payload: any[] };

export interface SubmissionTableState extends BaseTableState {
	viewingDrafts: boolean;
	viewingDeleted: boolean;
	formsArr: IForm[];
	selectedForm: number | undefined;
	selectedFormMode: string;
	selectedSubmissionType: string; // "3" for Asset Tagging, "7" for Property Condition Assessment
	filters: Record<string, unknown>;
	filterOptions: FilterOptions | null;
	showDeleteModal: boolean;
	isViewingQAForm: boolean;
	refetchTrigger: number;
	isLoading: boolean;
	columnsShown: Record<string, boolean>;
}

/* ---------------------------------------------------------------------
   Initial State + Reducer
   --------------------------------------------------------------------- */
const initialState: SubmissionTableState = {
	...baseInitialState,
	tableColumns: [],
	sortBy: "submitted_date",
	sortOrder: "desc",
	viewingDrafts: false,
	viewingDeleted: false,
	formsArr: [],
	selectedForm: undefined,
	isViewingQAForm: false,
	selectedFormMode: "submitted", // Default to submitted submissions
	selectedSubmissionType: "3", // Default to Asset Tagging
	filters: {},
	filterOptions: null,
	showDeleteModal: false,
	refetchTrigger: 0,
	isLoading: true,
	columnsShown: {},
};

/**
 * Merges only those fields from `newValues` which are actually present (defined).
 * Other fields in `oldState` remain untouched.
 */
function partialMerge<T extends object>(oldState: T, newValues: Partial<T>): T {
	const updated: T = { ...oldState };
	for (const [key, val] of Object.entries(newValues) as [
		keyof T,
		T[keyof T],
	][]) {
		if (val !== undefined) {
			updated[key] = val;
		}
	}
	return updated;
}

function reducer(
	state: SubmissionTableState,
	action: SubmissionTableAction,
): SubmissionTableState {
	switch (action.type) {
		case "SET_VIEWING_DRAFTS":
			if (state.viewingDrafts !== action.payload) {
				return { ...state, viewingDrafts: action.payload };
			}
			return state;
		case "SET_VIEWING_DELETED":
			if (state.viewingDeleted !== action.payload) {
				return { ...state, viewingDeleted: action.payload };
			}
			return state;
		case "SET_IS_VIEWING_QA_FORM":
			if (state.isViewingQAForm !== action.payload) {
				return { ...state, isViewingQAForm: action.payload };
			}
			return state;
		case "SET_FORMS_ARR":
			if (JSON.stringify(state.formsArr) !== JSON.stringify(action.payload)) {
				return { ...state, formsArr: action.payload as unknown as IForm[] };
			}
			return state;
		case "SET_SELECTED_FORM":
			if (state.selectedForm !== action.payload) {
				return { ...state, selectedForm: action.payload };
			}
			return state;
		case "SET_SELECTED_FORM_MODE":
			if (state.selectedFormMode !== action.payload) {
				return { ...state, selectedFormMode: action.payload };
			}
			return state;
		case "SET_SELECTED_SUBMISSION_TYPE":
			if (state.selectedSubmissionType !== action.payload) {
				return { ...state, selectedSubmissionType: action.payload };
			}
			return state;
		case "SET_IS_LOADING":
			if (state.isLoading !== action.payload) {
				return { ...state, isLoading: action.payload };
			}
			return state;
		case "SET_FILTERS":
			if (JSON.stringify(state.filters) !== JSON.stringify(action.payload)) {
				return { ...state, filters: action.payload };
			}
			return state;
		case "SET_FILTER_OPTIONS":
			return { ...state, filterOptions: action.payload };
		case "SET_SHOW_DELETE_MODAL":
			return { ...state, showDeleteModal: action.value };
		case "BATCH_UPDATE":
			return partialMerge(state, action.payload);
		case "SET_REFETCH_TRIGGER":
			return { ...state, refetchTrigger: action.payload };
		case "SET_COLUMNS_SHOWN":
			return { ...state, columnsShown: action.payload };
		case "SET_PAGE_NUM":
			return { ...state, pageNum: action.payload };
		case "SET_TABLE_COLUMNS":
			return { ...state, tableColumns: action.payload };
		default:
			return baseTableReducer(state, action as BaseTableAction);
	}
}

/* ---------------------------------------------------------------------
   SubmissionsTable
   --------------------------------------------------------------------- */
const SubmissionsTable: FC<SubmissionsTableProps> = ({
	organization,
	browserLocationId,
}) => {
	const { user } = useUserStore();
	const location = useLocation();
	const navigate = useNavigate();
	const [showChecklistZip, setShowChecklistZip] = useState(false);

	// Memoize preserved state
	const preservedState = useMemo(
		() => location?.state?.submissionsTableParams,
		[location?.state?.submissionsTableParams],
	);

	// Add a ref to track if we've handled the initial preserved state
	const initialPreservedStateHandledRef = useRef(false);

	// Memoize initial state
	const initialTableState = useMemo(() => {
		const forms = user?.organization?.forms ?? [];
		const numericOrgId = organization?.id || user?.organization?.id;
		const isOrg28 = numericOrgId === 28;

		// For org 28, default to "2" (Handyman Checklist) instead of "3" (Asset Tagging)
		const defaultSubmissionType = isOrg28
			? "2"
			: initialState.selectedSubmissionType;
		const selectedSubmissionType =
			preservedState?.selectedSubmissionType ?? defaultSubmissionType;

		// Use selectedSubmissionType to determine default form
		let defaultForm: number | undefined;

		if (forms.length > 0) {
			if (selectedSubmissionType === "7") {
				// Property Condition Assessment (all orgs)
				defaultForm = forms.find(
					(form) => (form.formId || form.form_id) === 7,
				)?.formId;
			} else if (isOrg28) {
				// Org 28: Default to Handyman Checklist (form ID 2), then QC Checklist (form ID 4)
				defaultForm =
					forms.find((form) => (form.formId || form.form_id) === 2)?.formId ||
					forms.find((form) => (form.formId || form.form_id) === 4)?.formId ||
					forms[0]?.formId;
			} else {
				// Other orgs: Default to Asset Tagging (form ID 3)
				defaultForm = forms.find(
					(form) => (form.formId || form.form_id) === 3,
				)?.formId;
			}

			// Fallback if preferred form not found
			if (!defaultForm) {
				if (isOrg28) {
					defaultForm = forms[0]?.formId;
				} else {
					defaultForm =
						forms.find((form) => (form.formId || form.form_id) === 3)?.formId || // Prefer form ID 3
						forms.find((form) => (form.formId || form.form_id) === 7)?.formId || // Then form ID 7
						forms.find(
							(form) => form.config?.formName === "Inventory & Tagging",
						)?.formId ||
						forms.find(
							(form) => form.config?.formName === "Inventory & Tagging Form",
						)?.formId ||
						forms[0]?.formId;
				}
			}
		}

		return {
			...initialState,
			pageNum: preservedState?.pageNum ?? initialState.pageNum,
			pageSize: preservedState?.pageSize ?? initialState.pageSize,
			searchVal: preservedState?.searchVal ?? initialState.searchVal,
			sortBy: preservedState?.sortBy ?? initialState.sortBy,
			sortOrder: preservedState?.sortOrder ?? initialState.sortOrder,
			selectedFormMode: (() => {
				const preserved = preservedState?.selectedFormMode;
				// Convert old values to new format
				if (preserved === "0") return "submitted";
				if (preserved === "1") return "qa";
				if (preserved === "2") return "drafts";
				// For org 28, default to pending_approval if no preserved state
				if (!preserved && isOrg28) return "pending_approval";
				return preserved ?? initialState.selectedFormMode;
			})(),
			selectedSubmissionType: selectedSubmissionType,
			selectedForm: preservedState?.selectedForm ?? defaultForm,
			viewingDeleted:
				preservedState?.viewingDeleted ?? initialState.viewingDeleted,
			filters: preservedState?.filters ?? initialState.filters,
			filterOptions:
				preservedState?.filterOptions ?? initialState.filterOptions,
			tableColumns: getOrgColumns(organization, undefined),
			columnsShown: initializeColumnsShown(
				getOrgColumns(organization, undefined),
			),
			formsArr: forms as IForm[],
		};
	}, [organization, preservedState, user?.organization?.forms]);

	const [state, dispatch] = useReducer(reducer, initialTableState);

	// Add a ref to track the last refresh timestamp
	const lastRefreshRef = useRef<number | null>(null);
	// Add a ref to track if we're currently refreshing
	const isRefreshingRef = useRef(false);

	// Create a ref to store the current state for the refresh effect
	const stateRef = useRef(state);
	stateRef.current = state;

	// Check for refresh query parameter and trigger a refetch if it changed
	useEffect(() => {
		const refresh = new URLSearchParams(location.search).get("refresh");
		if (
			refresh &&
			Number(refresh) !== lastRefreshRef.current &&
			!isRefreshingRef.current
		) {
			lastRefreshRef.current = Number(refresh);
			isRefreshingRef.current = true;

			// Set loading state before fetching
			setLoading("data", true);

			// Use the ref to get current state without causing dependency issues
			fetchSubmissionTableData(stateRef.current, dispatch, browserLocationId)
				.then(() => {
					setLoading("data", false);
					// Clear the refresh parameter
					navigate(location.pathname, { replace: true });
				})
				.catch((error) => {
					console.error("Error fetching data:", error);
					setLoading("data", false);
					// Clear the refresh parameter even if there's an error
					navigate(location.pathname, { replace: true });
				})
				.finally(() => {
					isRefreshingRef.current = false;
				});
		}
	}, [
		location.search,
		browserLocationId,
		dispatch,
		navigate,
		location.pathname,
	]);

	// Memoize form change handler
	const handleFormChange = useCallback((formId: number) => {
		dispatch({
			type: "SET_SELECTED_FORM",
			payload: formId,
		});
		dispatch({
			type: "SET_PAGE_NUM",
			payload: 1,
		});
	}, []);

	// Memoize search handler
	const handleSearch = useCallback((value: string) => {
		dispatch({ type: "SET_SEARCH_VAL", payload: value });
	}, []);

	// Memoize fetch dependencies
	const fetchDependencies = useMemo(
		() => ({
			selectedForm: state.selectedForm,
			selectedFormMode: state.selectedFormMode,
			viewingDrafts: state.viewingDrafts,
			viewingDeleted: state.viewingDeleted,
			sortBy: state.sortBy,
			sortOrder: state.sortOrder,
			pageNum: state.pageNum,
			pageSize: state.pageSize,
			searchVal: state.searchVal,
			filters: state.filters,
			refetchTrigger: state.refetchTrigger,
		}),
		[
			state.selectedForm,
			state.selectedFormMode,
			state.viewingDrafts,
			state.viewingDeleted,
			state.sortBy,
			state.sortOrder,
			state.pageNum,
			state.pageSize,
			state.searchVal,
			state.filters,
			state.refetchTrigger,
		],
	);

	// Add a loading ref to track concurrent loading states
	const loadingRef = useRef<{
		assetTypes: boolean;
		data: boolean;
	}>({
		assetTypes: false,
		data: false,
	});

	// Consolidate loading state management
	const setLoading = useCallback(
		(type: "assetTypes" | "data", isLoading: boolean) => {
			loadingRef.current[type] = isLoading;
			const isAnyLoading = Object.values(loadingRef.current).some(Boolean);
			dispatch({ type: "SET_IS_LOADING", payload: isAnyLoading });
		},
		[],
	);

	// Get organization ID from prop or user store (for API calls)
	const organizationId =
		organization?.external_firebase_id ||
		user?.organization?.external_firebase_id;

	// Get numeric organization ID for conditional logic (org 28 check)
	const numericOrgId = organization?.id || user?.organization?.id;

	// Set default submission type for org 28 when organization ID becomes available
	useEffect(() => {
		const isOrg28 = numericOrgId === 28;
		if (isOrg28 && state.selectedSubmissionType === "3") {
			// If org 28 and still showing "3", change to "2" (Handyman Checklist)
			dispatch({ type: "SET_SELECTED_SUBMISSION_TYPE", payload: "2" });
		} else if (
			!isOrg28 &&
			(state.selectedSubmissionType === "2" ||
				state.selectedSubmissionType === "4")
		) {
			// If not org 28 but showing org 28 form IDs, change to "3" (Asset Tagging)
			dispatch({ type: "SET_SELECTED_SUBMISSION_TYPE", payload: "3" });
		}
	}, [numericOrgId, state.selectedSubmissionType, dispatch]);

	// Update selectedForm when selectedSubmissionType changes
	useEffect(() => {
		if (state.formsArr.length > 0 && state.selectedSubmissionType) {
			const forms = state.formsArr;
			const submissionTypeFormId = parseInt(state.selectedSubmissionType);
			const matchingForm = forms.find(
				(form) => (form.formId || form.form_id) === submissionTypeFormId,
			);

			if (matchingForm && state.selectedForm !== matchingForm.formId) {
				dispatch({
					type: "SET_SELECTED_FORM",
					payload: matchingForm.formId as number,
				});
			}
		}
	}, [
		state.selectedSubmissionType,
		state.formsArr,
		state.selectedForm,
		dispatch,
	]);

	// Set default form when forms are loaded (similar to AssetsTable pattern)
	useEffect(() => {
		if (state.formsArr.length > 0 && !state.selectedForm) {
			const forms = state.formsArr;
			const isOrg28 = numericOrgId === 28;

			// Forms use formId (camelCase) for template form ID, form_id for database ID
			// Use selectedSubmissionType to determine which form to default to
			let defaultForm: number | undefined;

			if (state.selectedSubmissionType === "7") {
				// Property Condition Assessment (all orgs)
				defaultForm = forms.find(
					(form) => (form.formId || form.form_id) === 7,
				)?.formId;
			} else if (isOrg28) {
				// Org 28: Default to Handyman Checklist (form ID 2), then QC Checklist (form ID 4)
				defaultForm =
					forms.find((form) => (form.formId || form.form_id) === 2)?.formId ||
					forms.find((form) => (form.formId || form.form_id) === 4)?.formId ||
					forms[0]?.formId;
			} else {
				// Other orgs: Default to Asset Tagging (form ID 3)
				defaultForm = forms.find(
					(form) => (form.formId || form.form_id) === 3,
				)?.formId;
			}

			// Fallback if preferred form not found
			if (!defaultForm) {
				if (isOrg28) {
					defaultForm = forms[0]?.formId;
				} else {
					defaultForm =
						forms.find((form) => (form.formId || form.form_id) === 3)?.formId || // Prefer form ID 3
						forms.find((form) => (form.formId || form.form_id) === 7)?.formId || // Then form ID 7
						forms.find(
							(form) => form.config?.formName === "Inventory & Tagging",
						)?.formId ||
						forms.find(
							(form) => form.config?.formName === "Inventory & Tagging Form",
						)?.formId ||
						forms[0]?.formId;
				}
			}

			if (defaultForm) {
				dispatch({
					type: "SET_SELECTED_FORM",
					payload: defaultForm as number,
				});
			}
		}
	}, [
		state.formsArr.length,
		state.selectedForm,
		state.selectedSubmissionType,
		numericOrgId,
	]);

	// Load asset types if needed (similar to AssetsTable pattern)
	useEffect(() => {
		if (!state.filterOptions?.assetTypes && organizationId) {
			selfOrganizationAssetTypes(false).then((assetTypes) => {
				dispatch({ type: "SET_FILTER_OPTIONS", payload: { assetTypes } });
			});
		}
	}, [organizationId]);

	// Handle preserved state on mount
	useEffect(() => {
		if (!initialPreservedStateHandledRef.current && preservedState) {
			initialPreservedStateHandledRef.current = true;
			const convertedState = { ...preservedState };
			// Convert old form mode values
			if (convertedState.selectedFormMode === "0")
				convertedState.selectedFormMode = "submitted";
			if (convertedState.selectedFormMode === "1")
				convertedState.selectedFormMode = "qa";
			if (convertedState.selectedFormMode === "2")
				convertedState.selectedFormMode = "drafts";
			dispatch({ type: "BATCH_UPDATE", payload: convertedState });
		}
	}, [preservedState]);

	// NOTE: Cache clearing removed - rely on 30-second cache expiration and cache key mechanism
	// Clearing cache on mount caused unnecessary re-fetches, especially in React StrictMode
	// If fresh data is needed, the cache key will change based on search params/location
	// Or use clearOrganizedCache() explicitly after mutations (create/update/delete submissions)

	// Ref to track if initial fetch has been triggered (prevents multiple calls on mount)
	const initialFetchTriggeredRef = useRef(false);
	// Ref to track the last fetch key to prevent duplicate fetches
	const lastFetchKeyRef = useRef<string>("");

	// Fetch data whenever dependencies change (similar to AssetsTable pattern)
	useEffect(() => {
		// Only fetch if we have the necessary dependencies
		if (
			state.selectedForm &&
			state.selectedFormMode &&
			state.formsArr.length > 0
		) {
			// Create a fetch key from the current state to detect actual changes
			const fetchKey = JSON.stringify({
				pageNum: state.pageNum,
				pageSize: state.pageSize,
				sortBy: state.sortBy,
				sortOrder: state.sortOrder,
				searchVal: state.searchVal,
				filters: state.filters,
				viewingDeleted: state.viewingDeleted,
				viewingDrafts: state.viewingDrafts,
				selectedForm: state.selectedForm,
				selectedFormMode: state.selectedFormMode,
				browserLocationId,
				refetchTrigger: state.refetchTrigger,
			});

			// Skip if this is the exact same fetch we just did
			if (lastFetchKeyRef.current === fetchKey) {
				return;
			}

			// Update refs
			lastFetchKeyRef.current = fetchKey;
			if (!initialFetchTriggeredRef.current) {
				initialFetchTriggeredRef.current = true;
			}

			setLoading("data", true);
			fetchSubmissionTableData(state, dispatch, browserLocationId)
				.then(() => {
					setLoading("data", false);
				})
				.catch((error) => {
					console.error("[SubmissionsTable] Error fetching data:", error);
					setLoading("data", false);
				});
		}
	}, [
		state.pageNum,
		state.pageSize,
		state.sortBy,
		state.sortOrder,
		state.searchVal,
		state.filters,
		state.viewingDeleted,
		state.viewingDrafts,
		state.selectedForm,
		state.selectedFormMode,
		state.selectedSubmissionType,
		state.formsArr.length,
		browserLocationId,
		state.refetchTrigger,
	]);

	// Memoize row click handler
	const handleRowClick = useCallback(
		(record: SubmissionData) => {
			const isAdminView = location.pathname.includes("admin");
			const locId = browserLocationId || "";

			if (state.viewingDrafts) {
				navigate(`/forms/${record.form_id}`, {
					state: {
						responseData: record?.raw_input,
						draftId: record.id,
						formId: record.form_id,
						isSubmissionCompleted: Boolean(record?.end_time),
						locationId: record.location?.id,
						savedLocation: record.location,
						chosenAssetType: record?.raw_input?.assetType,
						userLocation: record?.draft?.userLocation,
						isQA: false,
						fromDraftsTable: state.viewingDrafts,
						filters: state.filters,
						filterOptions: state.filterOptions,
						formsArr: state.formsArr as IForm[],
					},
				});
				return;
			}

			navigate(
				isAdminView
					? `/admin/submissions/${record.id}`
					: `/locations/${locId}/submissions/${record.id}`,
				{
					state: {
						formId: record.form_id || state.selectedForm, // Use submission's form_id, fallback to selectedForm
						locationId: locId,
						isAdminView,
						submissionsTableParams: {
							pageNum: state.pageNum,
							pageSize: state.pageSize,
							sortBy: state.sortBy,
							sortOrder: state.sortOrder,
							searchVal: state.searchVal,
							viewingDeleted: state.viewingDeleted,
							selectedFormMode: state.selectedFormMode,
							viewingDrafts: state.viewingDrafts,
							selectedForm: state.selectedForm,
							filters: state.filters,
							filterOptions: state.filterOptions,
							formsArr: state.formsArr as IForm[],
						},
					},
				},
			);
		},
		[
			browserLocationId,
			location.pathname,
			navigate,
			state.filters,
			state.filterOptions,
			state.formsArr,
			state.pageNum,
			state.pageSize,
			state.searchVal,
			state.selectedForm,
			state.selectedFormMode,
			state.sortBy,
			state.sortOrder,
			state.viewingDeleted,
			state.viewingDrafts,
		],
	);

	// Callback to trigger data refresh after approving a submission
	const handleApproveCallback = useCallback(() => {
		clearOrganizedCache();
		dispatch({ type: "SET_REFETCH_TRIGGER", payload: Date.now() });
	}, []);

	// Memoize columns with approval callback for org 28
	const memoizedColumns = useMemo(() => {
		return getOrgColumns(organization, state, handleApproveCallback);
	}, [
		organization,
		state.selectedFormMode,
		state.selectedForm,
		handleApproveCallback,
	]);

	// Update state with new columns when they change
	useEffect(() => {
		dispatch({ type: "SET_TABLE_COLUMNS", payload: memoizedColumns });
	}, [memoizedColumns]);

	// Memoize actual data source to prevent flashing
	const actualDataSource = useMemo(() => {
		if (
			state.isLoading ||
			loadingRef.current.data ||
			loadingRef.current.assetTypes
		) {
			return Array(state.pageSize || 10).fill({ isLoading: true });
		}
		return state.dataSource;
	}, [state.isLoading, state.dataSource, state.pageSize]);

	// Create table props directly - match AssetsTable approach (no memoization)
	const tableProps = getTableProps(state, dispatch, {
		handleRowClick,
		dataSource: actualDataSource,
	});

	/* ---------------------------------------------------------------------
     Modal toggles & Export
     --------------------------------------------------------------------- */
	const handleExportCsv = useCallback(async () => {
		if (!state.selectedForm) {
			message.error("Please select a form to export.");
			return;
		}

		message.info("Exporting CSV...");

		try {
			// Map form mode to API parameters
			const formModeValue = state.selectedFormMode;
			let isApproved: boolean | undefined = undefined;
			let formMode: number | undefined = undefined;

			// Handle string-based modes (org 28 approval workflow)
			// Note: For pending_approval/approved, we also need to ensure is_qa_flagged=false
			let isQaFlagged: boolean | undefined = undefined;

			if (formModeValue === "pending_approval") {
				isApproved = false;
				isQaFlagged = false; // Exclude QA flagged submissions
				formMode = 0; // Submitted mode
			} else if (formModeValue === "approved") {
				isApproved = true;
				isQaFlagged = false; // Exclude QA flagged submissions
				formMode = 0; // Submitted mode
			} else if (formModeValue === "qa") {
				isQaFlagged = true; // Only QA flagged submissions
				formMode = 1; // QA mode
			} else if (formModeValue === "drafts") {
				formMode = 2; // Drafts mode
			} else if (formModeValue === "submitted") {
				formMode = 0; // Normal submitted mode
			} else {
				// Handle numeric modes (backward compatibility)
				const parsedMode = parseInt(formModeValue, 10);
				if (!isNaN(parsedMode)) {
					formMode = parsedMode;
				}
			}

			const blob = await exportSubmissionsCsvApi({
				form_id: String(state.selectedForm),
				form_mode: formMode,
				offset: 0,
				limit: 25000,
				sort: state.sortBy,
				sort_direction: state.sortOrder,
				location_id: browserLocationId || undefined, // Only include if set
				include_deleted: state.viewingDeleted,
				is_approved: isApproved,
				is_qa_flagged: isQaFlagged,
				search_value: state.searchVal || undefined,
			});

			const blobUrl = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = blobUrl;
			link.download = `submissions_export_${Date.now()}.csv`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(blobUrl);

			message.success("CSV export completed.");
		} catch (err) {
			console.error("CSV export failed:", err);
			message.error("Failed to export CSV");
		}
	}, [
		state.selectedForm,
		state.selectedFormMode,
		state.sortBy,
		state.sortOrder,
		state.viewingDeleted,
		state.searchVal,
		browserLocationId,
	]);

	const toggleDeleteModal = useCallback(() => {
		dispatch({ type: "SET_SHOW_DELETE_MODAL", value: !state.showDeleteModal });
	}, [state.showDeleteModal]);

	// Add new handlers
	const handleSubmissionTypeChange = useCallback((type: string) => {
		dispatch({ type: "SET_PAGE_NUM", payload: 1 });
		dispatch({ type: "SET_SELECTED_SUBMISSION_TYPE", payload: type });
		// When changing submission type, also update the selectedForm to match
		dispatch({ type: "SET_SELECTED_FORM", payload: parseInt(type) });
	}, []);

	const handleModeChange = useCallback(
		(mode: string, isQAForm: boolean, isDraft: boolean) => {
			dispatch({ type: "SET_PAGE_NUM", payload: 1 });
			dispatch({ type: "SET_SELECTED_FORM_MODE", payload: mode });
			dispatch({ type: "SET_IS_VIEWING_QA_FORM", payload: isQAForm });
			dispatch({ type: "SET_VIEWING_DRAFTS", payload: isDraft });
		},
		[],
	);

	const handleViewDeletedChange = useCallback((checked: boolean) => {
		dispatch({ type: "SET_VIEWING_DELETED", payload: checked });
	}, []);

	const handleColumnVisibilityChange = useCallback(
		(key: string, isVisible: boolean) => {
			dispatch({
				type: "SET_COLUMNS_SHOWN",
				payload: { ...state.columnsShown, [key]: isVisible },
			});
		},
		[state.columnsShown],
	);

	/* ---------------------------------------------------------------------
     Render
     --------------------------------------------------------------------- */
	return (
		<div className="table-card">
			<div className="table-header d-flex justify-content-between">
				<Search
					placeholder="Search"
					allowClear
					style={{ width: 300 }}
					onSearch={handleSearch}
					disabled={state.isLoading}
					defaultValue={state.searchVal}
				/>
				<Space>
					<SubmissionTypeSelect
						isLoading={state.isLoading}
						selectedSubmissionType={state.selectedSubmissionType}
						onSubmissionTypeChange={handleSubmissionTypeChange}
						organizationId={numericOrgId}
					/>
					<QASubmissionDraftSelect
						isLoading={state.isLoading}
						selectedFormMode={state.selectedFormMode}
						onModeChange={handleModeChange}
					/>
					<DeletedSwitch
						isLoading={state.isLoading}
						viewingDeleted={state.viewingDeleted}
						onViewDeletedChange={handleViewDeletedChange}
					/>
				</Space>
				<Space>
					<ColumnConfigDropdown
						columns={state.tableColumns}
						columnsShown={state.columnsShown}
						onColumnVisibilityChange={handleColumnVisibilityChange}
					/>
					<DeleteButton
						checkedItems={state.checkedItems || []}
						viewingDeleted={state.viewingDeleted}
						onClick={toggleDeleteModal}
					/>
					{organization.id === 28 && (
						<>
							<Button
								icon={<DownloadOutlined style={{ fontSize: "20px" }} />}
								type="text"
								onClick={handleExportCsv}
								disabled={!state.selectedForm || state.isLoading}
							/>
							<Tooltip title="Download checklists as PDFs (ZIP by location)">
								<Button
									icon={<FilePdfOutlined style={{ fontSize: "20px" }} />}
									type="text"
									onClick={() => setShowChecklistZip(true)}
									disabled={state.isLoading}
								/>
							</Tooltip>
						</>
					)}
				</Space>
			</div>

			{organization.id === 28 && (
				<ChecklistZipExportModal
					open={showChecklistZip}
					onClose={() => setShowChecklistZip(false)}
					browserLocationId={browserLocationId}
					defaultFormId={
						state.selectedForm ? Number(state.selectedForm) : undefined
					}
				/>
			)}

			<Divider />

			<DFPTable
				locale={{
					emptyText:
						!state.isLoading &&
						!loadingRef.current.data &&
						!loadingRef.current.assetTypes &&
						actualDataSource.length === 0 ? (
							<DFPEmpty
								className="w-100 h-100"
								style={{ minHeight: "50dvh" }}
								description={
									!state.selectedForm
										? "Please select a form to view submissions"
										: "No submissions returned. Please modify your search term or update your filters and try again."
								}
							/>
						) : null,
				}}
				{...tableProps}
			/>

			<DeleteModal
				state={state}
				dispatch={dispatch}
				browserLocationId={browserLocationId}
				tableType="submissions"
				fetchData={fetchSubmissionTableData}
			/>
		</div>
	);
};

export { SubmissionsTable };
